// ============================================================================
// SOP Kanban — Step 2 (2026-05-28) of cowork-new-account-sop-kanban-build.
//
// 5 active stage columns rendered left-to-right on desktop, two-up on tablet,
// stacked sections on mobile. Cards can be dragged between columns; the drop
// PATCHes new_account_sop.stage with optimistic UI + rollback on RLS denial.
//
// Stage list (active board, in order):
//   1. Info & Sub Checklist
//   2. Centralized Confirmation Call
//   3. Executive Review
//   4. Final Pre-Invoicing Check
//   5. Completed
//
// Cancelled / Stalled rows are NOT first-class columns. They appear in their
// active stage with a red border + 'Stalled' chip when status='Stalled', and
// disappear from the board (filter to surface them) when stage='Cancelled' or
// stage='Stalled'.
//
// DnD: @dnd-kit/core. Each column is a droppable; each card is a draggable.
// Pointer-only sensor with an 8px activation distance so taps still open the
// detail drawer instead of starting a drag accidentally on mobile.
//
// 2026-10-08 (item 818): each person can arrange THEIR view of this board —
// sort, pin, drag into their own order — through the shared board view
// control (src/shared/board-view). That arrangement is theirs alone, saved
// per person in the database, and changes no card. Nothing else about New
// Accounts changed: same module, same menu entry, same roles, same stages.
// ============================================================================

import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  NEW_ACCOUNT_SOP_STAGES,
  NEW_ACCOUNT_SOP_STAGE_LABELS,
  type NewAccountSop,
  type NewAccountSopActiveStage,
} from '../../shared/types';
import {
  arrangeCards,
  BoardCard,
  BoardViewControls,
  boardCollision,
  resolveBoardDrop,
  useBoardViewPrefs,
  type BoardViewApi,
} from '../../shared/board-view';
import SopCard from './sop-card';

interface Props {
  rows: NewAccountSop[];
  onSelect: (row: NewAccountSop) => void;
  /**
   * Optional. When provided the board supports drag-to-restage with
   * optimistic UI; without it cards still render but are not draggable.
   */
  onStageChange?: (sopId: string, nextStage: NewAccountSopActiveStage) => Promise<void> | void;
}

const sopFacts = (r: NewAccountSop) => ({
  id: r.sop_id,
  createdAt: r.created_at ?? null,
  startDate: r.start_date ?? null,
});

export default function SopKanban({ rows, onSelect, onStageChange }: Props) {
  const [activeDrag, setActiveDrag] = useState<NewAccountSop | null>(null);
  const sensors = useSensors(
    // 8px activation prevents tap-to-open from triggering a drag on mobile.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const view = useBoardViewPrefs('new-accounts');

  const columns = useMemo(() => {
    return NEW_ACCOUNT_SOP_STAGES.map((stage) => ({
      stage,
      label: NEW_ACCOUNT_SOP_STAGE_LABELS[stage],
      rows: arrangeCards(rows.filter((r) => r.stage === stage), sopFacts, view.prefs),
    }));
  }, [rows, view.prefs]);

  // Rows whose stage value isn't one of the 5 active stages — typically
  // 'Cancelled' or legacy values from the pre-2026-05-28 enum. Surface them
  // in an "Off-board" section so they aren't silently dropped from view
  // when a filter or migration mismatch hides them from the board.
  const offBoardRows = useMemo(
    () =>
      rows.filter(
        (r) => !NEW_ACCOUNT_SOP_STAGES.includes(r.stage as NewAccountSopActiveStage),
      ),
    [rows],
  );

  function handleDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    const row = rows.find((r) => r.sop_id === id);
    setActiveDrag(row ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    setActiveDrag(null);
    if (!onStageChange) return;
    if (!e.over) return;
    const sopId = String(e.active.id);
    const row = rows.find((r) => r.sop_id === sopId);
    if (!row) return;
    const drop = resolveBoardDrop(e.over.id, e.over.data.current as { columnId?: string } | undefined, row.stage ?? '');
    if (drop.kind === 'reorder') {
      // Dropped on a card in its own column: the person's order, nobody else's.
      const shown = columns.find((c) => c.stage === row.stage)?.rows.map((r) => r.sop_id) ?? [];
      view.place(shown, sopId, drop.overCardId);
      return;
    }
    if (drop.kind !== 'move') return;
    const nextStage = drop.columnId as NewAccountSopActiveStage;
    if (!NEW_ACCOUNT_SOP_STAGES.includes(nextStage)) return;
    if (row.stage === nextStage) return; // no-op drop on same column
    try {
      await onStageChange(sopId, nextStage);
    } catch (err) {
      // The patch hook already rolled back. Surface in console for now;
      // the parent surface logs through ErrorState on its own load path.
      console.error('[sop-kanban] stage change failed', err);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollision}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <BoardViewControls view={view} hasStartDate className="mb-3" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {columns.map((c) => (
          <KanbanColumn
            key={c.stage}
            stage={c.stage}
            label={c.label}
            rows={c.rows}
            onSelect={onSelect}
            draggable={Boolean(onStageChange)}
            view={view}
          />
        ))}
      </div>

      {offBoardRows.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
            Off-board · {offBoardRows.length}
            <span className="ml-2 font-normal normal-case tracking-normal text-tcs-text-muted">
              (cancelled or stage outside the active 5)
            </span>
          </h2>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {offBoardRows.map((r) => (
              <SopCard key={r.sop_id} row={r} onClick={onSelect} />
            ))}
          </ul>
        </section>
      )}

      {/* DragOverlay renders a floating preview of the card under the cursor.
          Without it, the card visually stays in its source column until drop. */}
      <DragOverlay>
        {activeDrag ? (
          <div className="w-72 rotate-1">
            <SopCard row={activeDrag} onClick={() => {}} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ----------------------------------------------------------------------------
// Column = droppable bucket. Cards are rendered as draggable items inside.
// ----------------------------------------------------------------------------

interface ColumnProps {
  stage: NewAccountSopActiveStage;
  label: string;
  rows: NewAccountSop[];
  onSelect: (row: NewAccountSop) => void;
  draggable: boolean;
  view: BoardViewApi;
}

function KanbanColumn({ stage, label, rows, onSelect, draggable, view }: ColumnProps) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const shownIds = rows.map((r) => r.sop_id);
  return (
    <section
      ref={setNodeRef}
      className={`min-w-0 rounded-lg border p-2 transition ${
        isOver
          ? 'border-tcs-text bg-tcs-bg-muted'
          : 'border-transparent'
      }`}
    >
      <h2 className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        <span className="truncate" title={label}>
          {label}
        </span>
        <span className="ml-2 shrink-0 rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] text-tcs-text-secondary">
          {rows.length}
        </span>
      </h2>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-tcs-border-strong bg-tcs-raised p-4 text-center text-xs text-tcs-text-muted">
          Empty.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <BoardCard
              key={r.sop_id}
              id={r.sop_id}
              columnId={stage}
              shownIds={shownIds}
              view={view}
              draggable={draggable}
              as="div"
            >
              <SopCard row={r} onClick={onSelect} />
            </BoardCard>
          ))}
        </ul>
      )}
    </section>
  );
}
